import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageLayout from "@/components/layout/PageLayout";
import TourCard from "@/components/TourCard";
import Seo from "@/components/Seo";
import WeddingInquiryDialog from "@/components/WeddingInquiryDialog";
import WeddingCostCalculator from "@/components/WeddingCostCalculator";
import { breadcrumbSchema, faqSchema, itemListSchema } from "@/components/JsonLd";
import { Price } from "@/context/CurrencyContext";
import { ArrowRight, Sparkles, Check, ChevronDown, ChevronUp, Download } from "lucide-react";

const WEDDING_THEMES = new Set(["wedding", "mock-wedding", "honeymoon"]);

const ItineraryCard = ({ itin, themeSlug, onInquire }) => {
  const [expanded, setExpanded] = useState(false);
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/themes/${themeSlug}/itineraries/${itin.id}/pdf`;
  return (
    <div
      className="bg-white border border-cream-200 rounded-sm overflow-hidden hover:shadow-lg transition-shadow"
      data-testid={`itinerary-${itin.id}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={itin.image} alt={itin.title} loading="lazy" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-marigold text-foreground text-[10px] font-semibold uppercase tracking-[0.2em]">
          {itin.nights} Nights
        </div>
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <h3 className="font-serif text-xl md:text-2xl leading-tight">{itin.title}</h3>
          <p className="text-white/85 text-sm mt-1">{itin.tagline}</p>
        </div>
      </div>
      <div className="p-6">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50">From</div>
            <div className="font-serif text-2xl text-deepred">
              <Price usd={itin.starting_price_usd} />
              <span className="text-xs text-foreground/50 font-sans ml-1.5">per couple</span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`itinerary-toggle-${itin.id}`}
            className="text-xs uppercase tracking-[0.2em] text-deepred hover:text-saffron inline-flex items-center gap-1"
          >
            {expanded ? "Hide" : "See"} day-by-day {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        </div>

        {/* Highlights */}
        <ul className="space-y-1.5 mb-5">
          {(itin.highlights || []).map((h, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-foreground/75">
              <Check className="w-4 h-4 text-saffron flex-shrink-0 mt-0.5" />
              <span>{h}</span>
            </li>
          ))}
        </ul>

        {/* Day-by-day */}
        {expanded && (
          <div className="border-t border-cream-200 pt-4 mb-5">
            <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-3">Day by day</div>
            <ol className="space-y-3">
              {(itin.days || []).map((d) => (
                <li key={d.day} className="flex gap-3">
                  <div className="text-[10px] font-semibold text-saffron mt-0.5 flex-shrink-0 w-10">D{d.day}</div>
                  <div>
                    <div className="text-sm font-medium text-foreground">{d.title}</div>
                    <div className="text-[13px] text-foreground/65 mt-0.5 leading-relaxed">{d.body}</div>
                  </div>
                </li>
              ))}
            </ol>
            {itin.includes && itin.includes.length > 0 && (
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.2em] text-foreground/50 mb-2">Includes</div>
                <div className="flex flex-wrap gap-2">
                  {itin.includes.map((inc, i) => (
                    <span key={i} className="text-[11px] px-2.5 py-1 bg-cream text-foreground/70 rounded-sm">{inc}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => onInquire(itin)}
          data-testid={`itinerary-book-${itin.id}`}
          className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-deepred text-white text-xs uppercase tracking-[0.25em] font-medium hover:bg-saffron transition-colors"
        >
          Book this journey <ArrowRight className="w-4 h-4" />
        </button>
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`itinerary-pdf-${itin.id}`}
          className="w-full mt-2 inline-flex items-center justify-center gap-2 px-6 py-2.5 border border-cream-200 text-foreground/75 text-[11px] uppercase tracking-[0.25em] font-medium hover:border-saffron hover:text-saffron transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Download PDF
        </a>
      </div>
    </div>
  );
};

const NotFoundBlock = ({ slug }) => (
  <PageLayout>
    <div className="container-tti py-32 text-center" data-testid="theme-not-found">
      <div className="eyebrow text-deepred mb-4">404</div>
      <h1 className="font-serif text-4xl md:text-5xl mb-4">Theme &ldquo;{slug}&rdquo; not found</h1>
      <p className="text-foreground/70 mb-8 max-w-md mx-auto">Try one of our curated themes below.</p>
      <div className="flex flex-wrap justify-center gap-3">
        {["yoga", "wildlife", "festivals"].map((s) => (
          <Link key={s} to={`/themes/${s}`} className="px-6 py-3 bg-deepred text-white text-sm uppercase tracking-wider hover:bg-saffron transition-colors">
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </Link>
        ))}
      </div>
    </div>
  </PageLayout>
);

export default function ThemePage() {
  const { slug } = useParams();
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    api
      .get(`/themes/${slug}`)
      .then((res) => alive && setManifest(res.data))
      .catch((e) => alive && (e.response?.status === 404 ? setNotFound(true) : setNotFound(true)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <PageLayout>
        <div className="min-h-[60vh] flex items-center justify-center" data-testid="theme-loading">
          <div className="animate-pulse text-foreground/50 text-sm uppercase tracking-[0.25em]">Loading…</div>
        </div>
      </PageLayout>
    );
  }

  if (notFound || !manifest) return <NotFoundBlock slug={slug} />;

  const canonical = `/themes/${manifest.slug}`;
  const jsonLd = [
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Themes", url: "/themes" },
      { name: manifest.title, url: canonical },
    ]),
    faqSchema(manifest.faqs || []),
  ];
  if (manifest.tours && manifest.tours.length > 0) {
    jsonLd.push(
      itemListSchema({
        name: manifest.title,
        items: manifest.tours.map((t) => ({
          name: t.title,
          url: `/tours/${t.slug}`,
        })),
      })
    );
  }

  return (
    <PageLayout>
      <Seo
        title={manifest.seo_title || manifest.title}
        description={manifest.seo_description}
        keywords={manifest.keywords}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section
        className="relative min-h-[75vh] flex items-end overflow-hidden"
        data-testid={`theme-hero-${manifest.slug}`}
      >
        <div className="absolute inset-0">
          <img
            src={manifest.hero_image}
            alt={manifest.title}
            className="w-full h-full object-cover animate-slow-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/85" />
        </div>
        <div className="relative container-tti pb-20 pt-32 z-10">
          <div className="max-w-4xl">
            <div className="eyebrow text-marigold mb-5 animate-fade-up flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" />
              {manifest.eyebrow}
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl text-white leading-[0.98] tracking-tight animate-fade-up">
              {manifest.title}
            </h1>
            <p
              className="text-white/85 text-lg md:text-xl max-w-2xl mt-6 font-light leading-relaxed animate-fade-up"
              style={{ animationDelay: "0.15s" }}
            >
              {manifest.tagline}
            </p>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 md:py-28 bg-cream" data-testid="theme-intro">
        <div className="container-tti grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">Overview</div>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight">
              Why travel with <span className="italic text-deepred">us</span> for this
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-foreground/80 text-lg font-light leading-relaxed">{manifest.intro}</p>
          </div>
        </div>
      </section>

      {/* SECTIONS */}
      <section className="py-16 md:py-24 bg-white" data-testid="theme-sections">
        <div className="container-tti grid md:grid-cols-3 gap-8">
          {(manifest.sections || []).map((s, i) => (
            <div key={i} className="border-l-2 border-saffron pl-6 py-2" data-testid={`theme-section-${i}`}>
              <div className="text-[10px] uppercase tracking-[0.25em] text-saffron mb-3">
                0{i + 1}
              </div>
              <h3 className="font-serif text-2xl mb-3 text-deepred">{s.heading}</h3>
              <p className="text-foreground/75 text-[15px] font-light leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WEDDING COST CALCULATOR — fun instant estimate, wedding theme only */}
      {manifest.slug === "wedding" && (
        <WeddingCostCalculator onInquire={() => setInquiryOpen(true)} />
      )}

      {/* SAMPLE ITINERARIES (Honeymoon and other packages with sample_itineraries) */}
      {manifest.sample_itineraries && manifest.sample_itineraries.length > 0 && (
        <section className="py-20 md:py-28 bg-cream" data-testid="theme-itineraries">
          <div className="container-tti">
            <div className="text-center mb-12 max-w-3xl mx-auto">
              <div className="eyebrow mb-4">Ready-to-book plans</div>
              <h2 className="font-serif text-3xl md:text-5xl leading-tight">
                Choose your <span className="italic text-deepred">perfect length</span>
              </h2>
              <p className="mt-4 text-foreground/70 text-lg font-light leading-relaxed">
                Sample itineraries starting points — every plan is fully customisable to your dates, budget and preferences.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {manifest.sample_itineraries.map((itin) => (
                <ItineraryCard key={itin.id} itin={itin} themeSlug={manifest.slug} onInquire={() => setInquiryOpen(true)} />
              ))}
            </div>
            <div className="text-center mt-10 text-sm text-foreground/60">
              Prices are per couple. Extendable and combinable — talk to a curator for a tailor-made plan.
            </div>
          </div>
        </section>
      )}

      {/* TOURS */}
      <section className="py-20 md:py-28 bg-white" data-testid="theme-tours">
        <div className="container-tti">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <div className="eyebrow mb-3">Curated Journeys</div>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight">
                {manifest.tours && manifest.tours.length > 0
                  ? "Featured itineraries for you"
                  : "Tailored trips available on request"}
              </h2>
            </div>
            <Link
              to="/tours"
              className="text-sm uppercase tracking-[0.25em] text-deepred hover:text-saffron transition-colors inline-flex items-center gap-2"
              data-testid="theme-tours-all-link"
            >
              Browse all journeys <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {manifest.tours && manifest.tours.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {manifest.tours.map((t) => (
                <TourCard key={t.id || t.slug} tour={t} />
              ))}
            </div>
          ) : (
            <div className="bg-cream border border-cream-200 rounded-sm p-10 text-center">
              <p className="text-foreground/70 mb-6 max-w-lg mx-auto">
                We are curating our next set of {manifest.slug} itineraries. In the meantime, our travel experts will
                build a bespoke journey around your interests, dates and budget — free of charge.
              </p>
              {WEDDING_THEMES.has(manifest.slug) ? (
                <button
                  type="button"
                  onClick={() => setInquiryOpen(true)}
                  className="inline-flex items-center gap-2 px-7 py-3 bg-deepred text-white text-sm uppercase tracking-wider hover:bg-saffron transition-colors"
                  data-testid="theme-inquiry-cta"
                >
                  Request a custom itinerary <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-7 py-3 bg-deepred text-white text-sm uppercase tracking-wider hover:bg-saffron transition-colors"
                  data-testid="theme-contact-cta"
                >
                  Request a custom itinerary <ArrowRight className="w-4 h-4" />
                </Link>
              )}
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      {manifest.faqs && manifest.faqs.length > 0 && (
        <section className="py-20 md:py-28 bg-white" data-testid="theme-faqs">
          <div className="container-tti grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="eyebrow mb-4">Questions & Answers</div>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight">
                What travellers <span className="italic text-deepred">ask us</span>
              </h2>
            </div>
            <div className="lg:col-span-8 space-y-8">
              {manifest.faqs.map((f, i) => (
                <div key={i} className="border-b border-cream-200 pb-6" data-testid={`theme-faq-${i}`}>
                  <h3 className="font-serif text-xl mb-3 text-deepred">{f.q}</h3>
                  <p className="text-foreground/75 text-[15px] font-light leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 md:py-28 bg-peacock text-white" data-testid="theme-cta">
        <div className="container-tti text-center max-w-3xl mx-auto">
          <div className="eyebrow text-marigold mb-5">Ready to journey</div>
          <h2 className="font-serif text-3xl md:text-5xl leading-tight mb-6">
            Start planning your {manifest.eyebrow ? manifest.eyebrow.toLowerCase() : "curated"} trip
          </h2>
          <p className="text-cream/80 text-lg font-light leading-relaxed mb-10">
            Tell us a few details and one of our travel curators will craft a private itinerary within 24 hours.
          </p>
          {WEDDING_THEMES.has(manifest.slug) ? (
            <button
              type="button"
              onClick={() => setInquiryOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-saffron text-white text-sm uppercase tracking-wider hover:bg-marigold hover:text-foreground transition-colors"
              data-testid="theme-plan-cta"
            >
              Plan my journey <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-saffron text-white text-sm uppercase tracking-wider hover:bg-marigold hover:text-foreground transition-colors"
              data-testid="theme-plan-cta"
            >
              Plan my journey <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </section>

      {/* Wedding / Mock-Wedding / Honeymoon multi-step inquiry dialog */}
      {WEDDING_THEMES.has(manifest.slug) && (
        <WeddingInquiryDialog
          open={inquiryOpen}
          onOpenChange={setInquiryOpen}
          defaultType={manifest.slug}
        />
      )}
    </PageLayout>
  );
}
